<template>
  <div id="file_editor_container" style="display: none;" class="modal-mask">
    <div>
      <div class="modal-container">
        <div class="modal-header">
          <slot name="header" class="text-center">
            Upload New Image
          </slot>
          <div class="file-info">
            <p><b>Important:</b> If you receive an error when adding an image, please check the following:</p>
            <p>The file name should only include letters and numbers (no special characters).</p>
            <p>Image requirements:</p>
            <ul>
              <li>File type: {{ config.imageRequirements.allowedFileTypeExtensions }}</li>
              <li>Width and height: {{ config.imageRequirements.minImageWidth }} x {{ config.imageRequirements.minImageHeight }} pixels or larger</li>
              <li>Size: at least {{ config.imageRequirements.minImageSizeKb }} KB</li>
            </ul>
            <p>The image will automatically be compressed down to the maximum file size. Or, you can resize the image yourself by using an online tool such as: <a target="_blank" href="https://picresize.com/">https://picresize.com</a></p>
          </div>
        </div>
      <div class="modal-body">
        <div v-for="(item, idx) of config.numberOfPhotos" :key="idx">
          <ImageProcessor 
            :parentTable="parentTable"
            :rowData="currentRowData"
            :colToUpdate="columnToUpdate"
            :config="config"
            :imageNumber="idx"
            :clearForm="clearForm"
            @update="displayPhoto($event)"
            @formHasCleared="formHasCleared($event)"
          />
        </div>
      </div>
        <div class="modal-footer">
          <slot name="footer">
            <button v-if="!['submitted_photos_staging', 'survey_photos_staging'].includes(parentTable)" class="btn btn-large btn-default btn-warning pull-left" v-on:click="removePhoto">
              Remove Photo
            </button>
            <p v-if="isRemoving" class="pull-left">Removing photo please wait...<i class="fas fa-spinner fa-spin pull-left" style="padding: 10px"></i> </p>
            <button v-if="multiplePhotos" class="btn btn-large btn-default btn-success text-left" v-on:click="closeEditorModal">
              Done
            </button>
            <button v-else class="btn btn-large btn-default btn-danger text-left" v-on:click="closeEditorModal">
              Cancel
            </button>
          </slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import ImageProcessor from "./ImageProcessor"

  export default {
    name: 'FileEditorContainer',
    components: {
      ImageProcessor
    },
    data() {
      return {
        isRemoving: false,
        isTikTokPeoplePhotos: this.$parent.table === 'tiktok_people_photos',
        parentTable: this.$parent.table,
        clearForm: false,
        numberFormsCleared: 0
      }
    },
    computed: {
      selectedRow() { return this.$parent.selectedCell.row; },
      selectedColumn() { return this.$parent.selectedCell.column; },
      currentRowData() { return this.$parent.hotdata[this.selectedRow]; },
      columnToUpdate() { return this.$parent.colToUpdate; },
      config() {
        switch(this.parentTable) {
          case 'submitted_photos_staging': // fallthrough to be same as below
          case 'survey_photos_staging':
            return {
              numberOfPhotos: 1,
              showLicensed: false,  // all files from this table are licensed--default value is set in ImageProcessor
              requireSource: true,
              imageRequirements: {
                allowedFileTypeExtensions: 'JPG, PNG, or GIF',
                allowedFileTypesForInput: 'image/*',
                minImageWidth: 1024,
                minImageHeight: 1024,
                minImageSizeKb: 1,
                maxCompressionSizeKb: 5000
              }
            };
          case 'tiktok_people_photos':
            return {
              numberOfPhotos: 5,
              showLicensed: false,
              requireSource: true,
              imageRequirements: {
                allowedFileTypeExtensions: 'JPG',
                allowedFileTypesForInput: 'image/jpeg',
                minImageWidth: 384,
                minImageHeight: 384,
                minImageSizeKb: 100,
                maxCompressionSizeKb: 150
              }
            };
          default:
            return {
              numberOfPhotos: 1,
              showLicensed: true,
              requireSource: false,
              imageRequirements: {
                allowedFileTypeExtensions: 'JPG, PNG, or GIF',
                allowedFileTypesForInput: 'image/*',
                minImageWidth: 100,
                minImageHeight: 100,
                minImageSizeKb: 1,
                maxCompressionSizeKb: 5000
              }
            }
        }
      },
      multiplePhotos() { return this.config.numberOfPhotos > 1; },
    },
    methods: {
      /**
       * Tells all ImageProcessor children components to clear their forms.
       */
      reset() {
        this.clearForm = true;
      },
      /**
       * Closes the file editor modal. Resets ImageProcessor forms.
       */
      closeEditorModal() {
        var elem = document.querySelector('#file_editor_container');
        elem.style.display = 'none';
        this.reset();
      },
      /**
       * Deletes or unsets photo from the table. The actual S3 object and row in the files table remain.
       * Once the photo is deleted/unset, the image displayed in the parent component is removed.
       *
       * people table: sets the photo column in people to null.
       * 
       * tiktok_people_photos table: deletes the row in tiktok_people_photos.
       * @async
       */
      async removePhoto() {

        this.isRemoving = true;
        let dataObject = {}; // Create Payload
        let method, payload;

        if (this.isTikTokPeoplePhotos) {
          // we need to get the tiktok_people_photos ID from the cell's object
          const colName = this.$parent.$refs.hot.hotInstance.getCellMeta(this.selectedRow, this.selectedColumn).prop.split('.')[0];
          const peoplePhotosId = parseInt(this.currentRowData[colName].id);

          dataObject.id = [peoplePhotosId];
          payload = {
            data: [dataObject],
            table: this.parentTable
          };
          method = 'DELETE';
        } else { // people etc
          //Get ID from row clicked
          dataObject.id = parseInt(this.currentRowData.id);
          
          // column name for file FK differs by table
          if (this.parentTable === 'organizations') {
            dataObject['logo'] = null;
          } else { // people
            dataObject['photo'] = null;
          }
          
          // Create payload
          payload = {
            data: [dataObject],
            table: this.parentTable
          };
          method = 'PATCH';
        }

        console.log(payload);
  
        let headers = {'Content-Type': 'application/json', 'Accept': 'application/json'};
        headers.resource = `${process.env.VUE_APP_API4_DOMAIN}/crud`;

        let response = await fetch(`${process.env.VUE_APP_API4_DOMAIN}/users/authorize`, {
          method: method,
          headers: headers,
          credentials: 'include',
          body: JSON.stringify(payload)
        });
        response = await response.json(); // Convert response to JSON
        if(!response.success) {
            console.log('Error deleting photo');
            this.isRemoving = false;
            this.closeEditorModal();
        } else {
          this.isRemoving = false;
          this.$emit('clearPhoto'); // Call Parent to set null in photo cell if successful.
          this.closeEditorModal();
        }

      },
      /**
       * Tells the parent component to display the event's new photo.
       * Also closes the modal if there is only one file to upload.
       * @param event 
       */
      displayPhoto(event) {
        this.$emit('update', event);
        if (this.config.numberOfPhotos === 1) {
          this.closeEditorModal();
        }
      },
      /**
       * Catches formHasCleared event from ImageProcessor child component.
       * Once all children forms have cleared, stops sending message to clear them
       */
      formHasCleared() {
        this.numberFormsCleared += 1;
        if (this.numberFormsCleared === this.config.numberOfPhotos) {
          this.numberFormsCleared = 0;
          this.clearForm = false;
        }
      }
    },
    mounted() {
      // load library for compressing images in the browser (https://github.com/WangYuLue/image-conversion)
      const imageCompressor = document.createElement('script')
      imageCompressor.setAttribute('src', 'https://cdn.jsdelivr.net/gh/WangYuLue/image-conversion/build/conversion.js')
      document.head.appendChild(imageCompressor);
      this.reset();
    },
  }
</script>

<style scoped>
.modal-mask {
  position: fixed;
  z-index: 9998;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, .5);
  display: table;
  transition: opacity .3s ease;
}
.modal-container {
  width: 50%;
  margin: 0px auto;
  padding: 20px 30px;
  background-color: #fff;
  border-radius: 2px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, .33);
  transition: all .3s ease;
  font-family: Helvetica, Arial, sans-serif;
  text-align: center;
  max-height: 100vh;
  overflow-y: scroll;
  overflow-x: hidden;
}
.modal-container .btn {
  margin: 0 auto;
}
.modal-header {
  margin-top: 0;
  color: #2c3e50;
  font-size: 30px;
}
.modal-body {
  margin: 20px 0;
  white-space: pre-line
}
.file-info {
  font-size: 12px;
  text-align: left;
}
</style>
